/**
 * L'état des deux zones de saisie des modèles de mail.
 *
 * Extrait du composant parce que c'est là qu'étaient les deux défauts, et
 * qu'aucun des deux ne se voyait à la lecture du JSX :
 *
 * 1. **les champs ne se remplissaient jamais au premier affichage.** La
 *    synchronisation ne se déclenchait qu'au CHANGEMENT de mail ou de langue,
 *    et la clé de départ était déjà la bonne : un modèle enregistré s'ouvrait
 *    donc sur des champs vides, comme s'il n'avait jamais été enregistré. Il
 *    réapparaissait en changeant de mail puis en revenant — ce qui rendait le
 *    défaut d'autant plus déroutant ;
 *
 * 2. **et sortir d'un champ vide effaçait le modèle.** L'enregistrement part
 *    au `blur`, sans regarder si quelque chose a bougé, et un champ vide veut
 *    dire « reviens au texte d'origine ». Conjugué au premier défaut, il
 *    suffisait d'ouvrir Réglages et de cliquer dans l'objet puis ailleurs pour
 *    perdre le modèle enregistré, sans l'avoir touché.
 *
 * Ces fonctions sont pures, et c'est tout l'intérêt : ce sont elles qu'on peut
 * vérifier, là où le composant demanderait un navigateur.
 */

/** Ce qu'un modèle enregistré porte, chaque champ pouvant manquer. */
export interface ModeleEnregistre {
  objet?: string;
  corps?: string;
}

/** Ce que les deux zones de saisie affichent, et pour quel mail. */
export interface VueModele {
  /** `type/langue` : ce qui identifie le modèle regardé. */
  cle: string;
  objet: string;
  corps: string;
}

/**
 * Ce que les champs DEVRAIENT afficher : le texte qui s'applique vraiment.
 *
 * **Le modèle d'origine sert de garnissage, pas seulement de suggestion.** Les
 * zones de saisie ne portaient que ce qui était ENREGISTRÉ, et le texte
 * d'origine n'apparaissait qu'en `placeholder` : à l'écran, un modèle jamais
 * modifié se lit alors en gris, s'efface à la première frappe, et il faut
 * retaper les huit lignes du mail pour en corriger une. Ce n'est pas une
 * édition, c'est une réécriture.
 *
 * Le champ porte donc le texte appliqué, d'où qu'il vienne. Le vider reste le
 * moyen de revenir au modèle d'origine : le champ se remplit alors à nouveau
 * de ce modèle, ce qui montre le résultat au lieu de le laisser deviner.
 *
 * **Pas de repli d'une langue sur l'autre, contrairement à `modeleMail`.**
 * Le moteur, lui, sert le français à défaut d'italien pour ne pas envoyer un
 * mail vide. Ici ce serait un piège : le champ italien se garnirait du texte
 * français enregistré, et l'enregistrer le figerait comme traduction.
 */
export function vueAttendue(
  cle: string,
  enregistre: ModeleEnregistre | undefined,
  defaut: { objet: string; corps: string },
): VueModele {
  return {
    cle,
    objet: enregistre?.objet?.trim() ? enregistre.objet : defaut.objet,
    corps: enregistre?.corps?.trim() ? enregistre.corps : defaut.corps,
  };
}

/**
 * Faut-il recharger les champs depuis ce qui est enregistré ?
 *
 * Oui dans deux cas : on regarde un AUTRE modèle — changement de mail ou de
 * langue —, ou celui qu'on regarde a changé ailleurs, ce qui arrive après
 * chaque enregistrement puisque les réglages sont relus.
 *
 * Non tant que rien n'a bougé, et c'est ce qui protège la frappe en cours :
 * comparer à ce qui était enregistré LA DERNIÈRE FOIS, plutôt qu'au contenu du
 * champ, évite d'écraser ce que l'utilisateur est en train de taper.
 */
export function aResynchroniser(vue: VueModele, attendue: VueModele): boolean {
  return vue.cle !== attendue.cle
    || vue.objet !== attendue.objet
    || vue.corps !== attendue.corps;
}

/**
 * Faut-il envoyer ce champ à l'API ?
 *
 * Seulement s'il diffère de ce qui est enregistré. Un `blur` sans modification
 * — entrer dans un champ, en sortir — ne doit rien écrire : il ferait un aller
 * -retour réseau pour rien dans le meilleur des cas, et effacerait le modèle
 * dans le pire, un champ vide valant « reviens au texte d'origine ».
 */
export function aEnregistrer(saisi: string, enregistre: string): boolean {
  return saisi !== enregistre;
}
